import type { AskQuestion } from './types';

import { findWordSequenceIndices } from 'js/src/agent/tools/transcript-word-match';
import type { TranscriptWord } from 'js/src/client';
import { resolveClipSource } from 'js/src/components/app/chat-tab/components/ask-user-question/clip-preview';
import { retrieveTranscriptForAsset } from 'js/src/data/clip-creator/clip-creator-utils';

/**
 * Checks every clip of a `clip_select` question against the recording it is cut
 * from, BEFORE the question is shown.
 *
 * A clip is located by finding its words in the transcript, so a quote that
 * tidies the recording up — dropping a stutter or an "um", as anything retyping
 * a passage tends to — matches nothing and its card arrives dead. Catching that
 * here turns it into an error the agent can fix on the spot, instead of a broken
 * card the user has to interpret.
 *
 * Returns an error message, or null when every clip resolves. Best-effort: if
 * the transcript can't be fetched, the question still goes ahead (its cards say
 * so for themselves).
 */
export async function validateClipQuestion(question: AskQuestion): Promise<string | null> {
    if (question.type !== 'clip_select') {
        return null;
    }
    const source = resolveClipSource(question.clip_source_media_id);
    if (!source) {
        return `${question.clip_source_media_id} is not a playable video or audio recording, so no clips could be cut from it. Pass the media id of the uploaded recording the clips come from — or, if it was only just uploaded, it may still be processing: wait a moment and ask again.`;
    }

    let transcriptWords: TranscriptWord[] | undefined;
    try {
        ({ transcriptWords } = await retrieveTranscriptForAsset(source.assetUuid, source.isAudio));
    } catch {
        return null;
    }
    if (!transcriptWords || transcriptWords.length === 0) {
        return null;
    }

    const unresolved = question.clips
        .map((clip) => ({
            clip,
            found: findWordSequenceIndices(transcriptWords, clip.words),
        }))
        .filter((entry) => !entry.found);

    if (unresolved.length === 0) {
        return null;
    }
    const details = unresolved
        .map(({ clip }) => `- "${clip.label}": "${clip.words.join(' ')}"`)
        .join('\n');
    return `These clips could not be located in the recording, so the question was not shown:\n${details}\nEach clip is found by matching its words against the transcript, so they have to be copied exactly as the transcript spells them — including filler words like "um" and any words the speaker repeats. Re-read the transcript, quote the passages without tidying them up, and ask again.`;
}
